import { DefenceType, GearSizeType, Rarity, WeaponType } from '../models/enums';
import { Filter } from '../models/filter-models';
import { GameItem } from '../models/game-item.models';
import { PresetsService } from './presets.service';
import { buildDefenceRules } from './generators/leveling-gear-generator.service';

export interface LevelingPresetExampleContext {
  poe1: boolean;
  areaLevel: number | null;
  lookupItemClass: (baseType: string) => string | undefined;
  getFirstBaseTypeForItemClass?: (itemClass: string) => string | undefined;
  getFirstBaseTypeForItemClassAndDefenceTypes?: (
    itemClass: string,
    defenceTypes: readonly DefenceType[],
  ) => string | undefined;
  getGearDefenceStats?: (baseType: string) => {
    baseArmour: number;
    baseEvasion: number;
    baseEnergyShield: number;
  } | undefined;
  getAllWeaponTypes?: () => string[];
  getOneHandWeaponTypes?: () => string[];
  getTwoHandWeaponTypes?: () => string[];
}

interface TierSimulationJob {
  cacheKey: string;
  item: GameItem;
}

const CAMPAIGN_PROGRESS_PRESETS = new Set([
  'presetEarlyCampaign1',
  'presetMidCampaign30',
  'presetLateCampaignEarlyMaps60',
]);

export function shouldBuildLevelingPresetExamples(filter: Filter, poe1: boolean): boolean {
  if (!filter.classPreset) return false;
  if (!poe1) return true;
  return !!filter.progressPreset && CAMPAIGN_PROGRESS_PRESETS.has(filter.progressPreset);
}

export function buildLevelingPresetExampleJobs(
  filter: Filter,
  context: LevelingPresetExampleContext,
): TierSimulationJob[] {
  if (!shouldBuildLevelingPresetExamples(filter, context.poe1)) return [];

  const jobs: TierSimulationJob[] = [];
  const areaLevel = context.areaLevel ?? 30;

  const weaponJob = buildLevelingWeaponJob(filter, context, areaLevel);
  if (weaponJob) jobs.push(weaponJob);

  const armourJob = buildLevelingArmourJob(filter, context, areaLevel);
  if (armourJob) jobs.push(armourJob);

  const specialWeaponJob = buildSpecialWeaponIgnoreLevelDiffJob(filter, context);
  if (specialWeaponJob) jobs.push(specialWeaponJob);

  const gemJob = buildFavouriteGemJob(filter, context);
  if (gemJob) jobs.push(gemJob);

  const vendoringRareJob = buildVendoringRareJob(filter, context);
  if (vendoringRareJob) jobs.push(vendoringRareJob);

  const vendoringMagicJob = buildVendoringMagicJob(filter, context);
  if (vendoringMagicJob) jobs.push(vendoringMagicJob);

  return jobs;
}

function buildLevelingWeaponJob(
  filter: Filter,
  context: LevelingPresetExampleContext,
  areaLevel: number,
): TierSimulationJob | null {
  const weaponClass = resolveFirstWeaponClass(filter, context);
  if (!weaponClass) return null;

  const baseType = context.getFirstBaseTypeForItemClass?.(weaponClass);
  if (!baseType) return null;

  // Stay within the current area-level band's DropLevel window (bands are every 5).
  const dropLevel = Math.max(1, areaLevel);
  const dims = dimensionsForItemClass(weaponClass, context);

  return {
    cacheKey: 'drop-preview-leveling-weapon',
    item: new GameItem({
      itemClass: weaponClass,
      baseType,
      rarity: Rarity.Rare,
      identified: false,
      dropLevel,
      width: dims.width,
      height: dims.height,
    }),
  };
}

function buildLevelingArmourJob(
  filter: Filter,
  context: LevelingPresetExampleContext,
  areaLevel: number,
): TierSimulationJob | null {
  if (!filter.showLevelingGearArmourDefenceTypes.length) return null;

  const itemClass = 'Helmets';
  const defenceTypes = filter.showLevelingGearArmourDefenceTypes as DefenceType[];
  const baseType = context.getFirstBaseTypeForItemClassAndDefenceTypes?.(itemClass, defenceTypes)
    ?? context.getFirstBaseTypeForItemClass?.(itemClass);
  if (!baseType) return null;

  // Stay within the current area-level band's DropLevel window (bands are every 5).
  const dropLevel = Math.max(1, areaLevel);
  const defence = defenceStatsForBaseType(baseType, context)
    ?? defencePropsForFilter(filter);

  return {
    cacheKey: 'drop-preview-leveling-armour',
    item: new GameItem({
      itemClass,
      baseType,
      rarity: Rarity.Magic,
      dropLevel,
      width: 2,
      height: 2,
      ...defence,
    }),
  };
}

function buildSpecialWeaponIgnoreLevelDiffJob(
  filter: Filter,
  context: LevelingPresetExampleContext,
): TierSimulationJob | null {
  if (context.poe1) return null;

  const specialClass = resolveFirstSpecialWeaponClass(filter, context);
  if (!specialClass) return null;

  const baseType = context.getFirstBaseTypeForItemClass?.(specialClass);
  if (!baseType) return null;

  const dims = dimensionsForItemClass(specialClass, context);

  return {
    cacheKey: 'drop-preview-leveling-special-weapon',
    item: new GameItem({
      itemClass: specialClass,
      baseType,
      rarity: Rarity.Magic,
      dropLevel: 1,
      width: dims.width,
      height: dims.height,
    }),
  };
}

function buildFavouriteGemJob(
  filter: Filter,
  context: LevelingPresetExampleContext,
): TierSimulationJob | null {
  if (!context.poe1) return null;

  const gemName = filter.showSearchedGemTypes.find(g => g && g !== 'None');
  if (!gemName) return null;

  const itemClass = context.lookupItemClass(gemName) ?? 'Skill Gems';

  return {
    cacheKey: 'drop-preview-leveling-favourite-gem',
    item: new GameItem({
      itemClass,
      baseType: gemName,
      rarity: Rarity.Normal,
    }),
  };
}

function buildVendoringRareJob(
  filter: Filter,
  context: LevelingPresetExampleContext,
): TierSimulationJob | null {
  const item = resolveVendoringExampleItem(filter, context, filter.showVendoringRaresSize, Rarity.Rare);
  if (!item) return null;
  return { cacheKey: 'drop-preview-leveling-vendoring-rare', item };
}

function buildVendoringMagicJob(
  filter: Filter,
  context: LevelingPresetExampleContext,
): TierSimulationJob | null {
  const item = resolveVendoringExampleItem(filter, context, filter.showVendoringMagicsSize, Rarity.Magic);
  if (!item) return null;
  return { cacheKey: 'drop-preview-leveling-vendoring-magic', item };
}

// Prefer gear that cannot match leveling weapon/armour rules (unselected weapon class
// or mismatched armour defence). Falls back to low drop-level armour when selections are All.
function resolveVendoringExampleItem(
  filter: Filter,
  context: LevelingPresetExampleContext,
  gearSize: GearSizeType,
  rarity: Rarity,
): GameItem | null {
  const size = sizeForGearSize(gearSize);

  const weaponClass = resolveUnselectedWeaponClassForVendoring(filter, context, size);
  if (weaponClass) {
    const baseType = context.getFirstBaseTypeForItemClass?.(weaponClass);
    if (baseType) {
      const dims = dimensionsForItemClass(weaponClass, context);
      return new GameItem({
        itemClass: weaponClass,
        baseType,
        rarity,
        identified: false,
        dropLevel: 1,
        width: Math.min(dims.width, size.width),
        height: Math.min(dims.height, size.height),
      });
    }
  }

  const armour = resolveNonMatchingArmourForVendoring(filter, context, size, rarity);
  if (armour) {
    return new GameItem({
      itemClass: armour.itemClass,
      baseType: armour.baseType,
      rarity,
      identified: false,
      dropLevel: 1,
      width: armour.width,
      height: armour.height,
      ...armour.defence,
    });
  }

  return null;
}

function resolveUnselectedWeaponClassForVendoring(
  filter: Filter,
  context: LevelingPresetExampleContext,
  size: { width: number; height: number },
): string | undefined {
  const allWeapons = context.getAllWeaponTypes?.() ?? [];
  if (!allWeapons.length) return undefined;

  // Weapons disabled for leveling → any weapon works (and won't hit leveling weapon rules).
  const candidates = !filter.showLevelingGear || !filter.showLevelingGearWeapons
    ? allWeapons
    : filter.showLevelingGearWeaponClasses.includes('All')
      ? []
      : allWeapons.filter(w => !filter.showLevelingGearWeaponClasses.includes(w));

  return candidates.find(weaponClass => {
    if (!context.getFirstBaseTypeForItemClass?.(weaponClass)) return false;
    const dims = dimensionsForItemClass(weaponClass, context);
    return dims.width <= size.width && dims.height <= size.height;
  });
}

function resolveNonMatchingArmourForVendoring(
  filter: Filter,
  context: LevelingPresetExampleContext,
  size: { width: number; height: number },
  rarity: Rarity,
): { itemClass: string; width: number; height: number; baseType: string; defence: Pick<GameItem, 'baseArmour' | 'baseEvasion' | 'baseEnergyShield'> } | null {
  // Prefer Helmets/Gloves (never Boots — those can match the leveling boots special rule).
  const itemClass = size.height <= 2
    ? (rarity === Rarity.Rare ? 'Helmets' : 'Gloves')
    : 'Body Armours';
  const dims = itemClass === 'Body Armours'
    ? { width: 2, height: Math.min(3, size.height) }
    : { width: 2, height: 2 };

  if (dims.width > size.width || dims.height > size.height) return null;

  const selected = filter.showLevelingGearArmourDefenceTypes as DefenceType[];
  const useMismatch = filter.showLevelingGear && filter.showLevelingGearArmour
    && selected.length > 0
    && !selected.includes(DefenceType.All);

  const mismatchDefence = useMismatch ? defencePropsForMismatch(selected) : null;
  const mismatchTypes = mismatchDefence ? defenceTypesForProps(mismatchDefence) : null;

  const baseType = (mismatchTypes
    ? context.getFirstBaseTypeForItemClassAndDefenceTypes?.(itemClass, mismatchTypes)
    : undefined)
    ?? context.getFirstBaseTypeForItemClass?.(itemClass);
  if (!baseType) return null;

  const defence = defenceStatsForBaseType(baseType, context)
    ?? mismatchDefence
    ?? { baseArmour: 1, baseEvasion: 0, baseEnergyShield: 0 };

  return { itemClass, width: dims.width, height: dims.height, baseType, defence };
}

const DEFENCE_COMBOS: Pick<GameItem, 'baseArmour' | 'baseEvasion' | 'baseEnergyShield'>[] = [
  { baseArmour: 1, baseEvasion: 0, baseEnergyShield: 0 },
  { baseArmour: 0, baseEvasion: 1, baseEnergyShield: 0 },
  { baseArmour: 0, baseEvasion: 0, baseEnergyShield: 1 },
  { baseArmour: 1, baseEvasion: 1, baseEnergyShield: 0 },
  { baseArmour: 1, baseEvasion: 0, baseEnergyShield: 1 },
  { baseArmour: 0, baseEvasion: 1, baseEnergyShield: 1 },
];

function defencePropsForMismatch(
  defenceTypes: DefenceType[],
): Pick<GameItem, 'baseArmour' | 'baseEvasion' | 'baseEnergyShield'> | null {
  const rules = buildDefenceRules(defenceTypes);
  return DEFENCE_COMBOS.find(combo => !rules.some(rule => matchesDefenceRule(combo, rule))) ?? null;
}

function matchesDefenceRule(
  props: Pick<GameItem, 'baseArmour' | 'baseEvasion' | 'baseEnergyShield'>,
  rule: { minArmour?: number; minEvasion?: number; minEnergyShield?: number },
): boolean {
  if (!matchesDefenceStat(props.baseArmour ?? 0, rule.minArmour)) return false;
  if (!matchesDefenceStat(props.baseEvasion ?? 0, rule.minEvasion)) return false;
  if (!matchesDefenceStat(props.baseEnergyShield ?? 0, rule.minEnergyShield)) return false;
  return true;
}

function matchesDefenceStat(value: number, min: number | undefined): boolean {
  if (min === undefined) return true;
  if (min === 0) return value === 0;
  return value >= min;
}

function resolveFirstWeaponClass(filter: Filter, context: LevelingPresetExampleContext): string | undefined {
  const selected = filter.showLevelingGearWeaponClasses;
  if (!selected.length) return undefined;

  if (selected.includes('All')) {
    return context.getAllWeaponTypes?.()?.[0];
  }

  return selected[0];
}

function resolveFirstSpecialWeaponClass(
  filter: Filter,
  context: LevelingPresetExampleContext,
): string | undefined {
  const special = PresetsService.specialWeaponTypes.map(w => w.toString());
  const selected = filter.showLevelingGearWeaponClasses;

  if (selected.includes('All')) {
    return special.find(c => context.getFirstBaseTypeForItemClass?.(c));
  }

  return selected.find(c => special.includes(c) && context.getFirstBaseTypeForItemClass?.(c));
}

function defenceStatsForBaseType(
  baseType: string,
  context: LevelingPresetExampleContext,
): Pick<GameItem, 'baseArmour' | 'baseEvasion' | 'baseEnergyShield'> | undefined {
  return context.getGearDefenceStats?.(baseType);
}

function defenceTypesForProps(
  props: Pick<GameItem, 'baseArmour' | 'baseEvasion' | 'baseEnergyShield'>,
): DefenceType[] {
  const hasArm = (props.baseArmour ?? 0) > 0;
  const hasEva = (props.baseEvasion ?? 0) > 0;
  const hasEs = (props.baseEnergyShield ?? 0) > 0;

  if (hasArm && hasEva && !hasEs) return [DefenceType.ArmourEvasion];
  if (hasArm && hasEs && !hasEva) return [DefenceType.ArmourEnergyShield];
  if (hasEva && hasEs && !hasArm) return [DefenceType.EvasionEnergyShield];
  if (hasArm && !hasEva && !hasEs) return [DefenceType.Armour];
  if (hasEva && !hasArm && !hasEs) return [DefenceType.Evasion];
  if (hasEs && !hasArm && !hasEva) return [DefenceType.EnergyShield];
  return [DefenceType.Armour];
}

function defencePropsForFilter(filter: Filter): Pick<GameItem, 'baseArmour' | 'baseEvasion' | 'baseEnergyShield'> {
  const rules = buildDefenceRules(filter.showLevelingGearArmourDefenceTypes as DefenceType[]);
  const rule = rules[0] ?? {};

  return {
    baseArmour: rule.minArmour === 0 ? 0 : (rule.minArmour === 1 || rule.minArmour === undefined ? 1 : 0),
    baseEvasion: rule.minEvasion === 0 ? 0 : (rule.minEvasion === 1 ? 1 : 0),
    baseEnergyShield: rule.minEnergyShield === 0 ? 0 : (rule.minEnergyShield === 1 ? 1 : 0),
  };
}

function sizeForGearSize(size: GearSizeType): { width: number; height: number } {
  switch (size) {
    case GearSizeType.TwoByTwoOnly:
      return { width: 2, height: 2 };
    case GearSizeType.MediumOnly:
      return { width: 2, height: 3 };
    case GearSizeType.JewelleryOnly:
      return { width: 1, height: 1 };
    default:
      return { width: 2, height: 3 };
  }
}

function dimensionsForItemClass(
  itemClass: string,
  context: LevelingPresetExampleContext,
): { width: number; height: number } {
  if (itemClass === WeaponType.Quivers || itemClass === 'Quivers') {
    return { width: 2, height: 3 };
  }
  if (context.getOneHandWeaponTypes?.().includes(itemClass)) {
    return { width: 1, height: 3 };
  }
  if (context.getTwoHandWeaponTypes?.().includes(itemClass)) {
    return { width: 2, height: 4 };
  }
  return { width: 2, height: 3 };
}
